import { useDeferredValue, useEffect, useLayoutEffect, useRef, useState, type FormEvent } from "react";
import { useAction, useMutation, usePaginatedQuery, useQuery } from "convex/react";
import { useSearchParams } from "react-router-dom";
import { Ban, CalendarClock, Check, ChevronDown, Copy, Edit3, History, Layers, Link2, Loader2, Plus, RotateCcw, Search, ShieldOff, Sparkles, Trash2 } from "lucide-react";
import { api } from "../../convex/_generated/api";
import type { Doc, Id } from "../../convex/_generated/dataModel";
import { Badge, Button, Card, ConfirmDialog, EmptyState, Field, Input, Modal, Notice, PageHeader, TableSkeleton } from "../components/ui";
import { CATEGORY_LABELS, actionLabel, formatDate, formatDateCompact, formatMonthYear, errorMessage } from "../lib/format";
import { RedateModal } from "../components/RedateModal";
import { buildAuditPresentation } from "../lib/auditDetails";

type Entry = Doc<"blacklist">;

/**
 * Une raison longue est repliée : sinon une seule ligne du tableau occupe l'écran. « Voir tout »
 * n'apparaît que si le texte est réellement coupé — la longueur seule ne le dit pas, la largeur
 * disponible change d'un endroit à l'autre.
 */
function ReasonCell({ reason }: { reason?: string }) {
  const [open, setOpen] = useState(false);
  const [cut, setCut] = useState(true);
  const text = useRef<HTMLParagraphElement>(null);
  const long = (reason?.length ?? 0) > 90;
  useLayoutEffect(() => {
    const node = text.current;
    if (!node || !long || open) return;
    const measure = () => setCut(node.scrollHeight > node.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [long, open, reason]);
  if (!reason) return <>—</>;
  const clamped = long && !open;
  return (
    <div className={`reason-cell${clamped ? " reason-cell--clamped" : ""}`}>
      <p ref={text} title={clamped && cut ? reason : undefined}>{reason}</p>
      {long && (cut || open) && <button type="button" onClick={() => setOpen((value) => !value)}>{open ? "Réduire" : "Voir tout"}</button>}
    </div>
  );
}

/**
 * Une valeur de la table, copiable d'un clic. Les identifiants font trente-deux caractères :
 * les sélectionner à la souris sans en perdre un bout est pénible, et une erreur de copie
 * rattache une sanction au mauvais joueur.
 */
function CopyValue({ value, mono }: { value?: string; mono?: boolean }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  if (!value) return <span className="copy-value copy-value--empty">—</span>;
  const copier = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1400);
    } catch { /* Le presse-papiers peut être refusé : la valeur reste sélectionnable à la main. */ }
  };
  return (
    <button type="button" className={`copy-value${mono ? " copy-value--id" : ""}${copied ? " copy-value--done" : ""}`}
      title={copied ? "Copié" : `Copier ${value}`} aria-label={`Copier ${value}`} onClick={() => void copier()}>
      <span>{value}</span>
      {copied ? <Check size={12} aria-hidden="true" /> : <Copy size={12} aria-hidden="true" />}
    </button>
  );
}
const emptyForm = { category: "cheat", discordId: "", discordPseudo: "", epicId: "", epicPseudo: "", serversBan: "", reason: "" };

/** `<input type="date">` attend AAAA-MM-JJ dans le fuseau local. */
function toDateInput(value?: number): string {
  const date = value ? new Date(value) : new Date();
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

/** Repositionne la date saisie à midi local, pour qu'un décalage horaire ne la fasse pas glisser d'un jour. */
function fromDateInput(value: string): number | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day, 12, 0, 0).getTime();
}

/**
 * Provenance d'une sanction : son auteur et sa date. Les entrées migrées portent l'horodatage
 * de l'import, jamais la date réelle du bannissement — le dire évite de laisser croire à une
 * précision que les feuilles sources ne contenaient pas.
 */
function EntryOrigin({ id }: { id: Id<"blacklist"> }) {
  const origine = useQuery(api.blacklist.entryAuthor, { id });
  if (!origine) return null;
  const auteur = origine.addedBy;
  return (
    <div className="entry-origin">
      <span className="entry-origin__who">
        {auteur?.avatarUrl
          ? <img src={auteur.avatarUrl} alt="" />
          : <span className="entry-origin__fallback">{(auteur?.name ?? "?").slice(0, 1).toUpperCase()}</span>}
        <span>
          <strong>Ajouté par {auteur?.name ?? "un compte supprimé"}</strong>
          <small>
            {origine.source === "migration" && !origine.updatedAt
              ? `Importé des feuilles en ${formatMonthYear(origine.addedAt)} — date réelle du bannissement inconnue`
              : `en ${formatMonthYear(origine.addedAt)}`}
          </small>
        </span>
      </span>
      {origine.updatedBy && origine.updatedAt && (
        <small className="muted">Modifié par {origine.updatedBy.name} en {formatMonthYear(origine.updatedAt)}</small>
      )}
      {origine.liftedBy && origine.liftedAt && (
        <small className="muted">Levé par {origine.liftedBy.name} en {formatMonthYear(origine.liftedAt)}</small>
      )}
    </div>
  );
}

function EntryForm({ entry, onClose }: { entry?: Entry; onClose: () => void }) {
  const create = useMutation(api.blacklist.create);
  const update = useMutation(api.blacklist.update);
  const lookup = useAction(api.wls.lookup);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lookupState, setLookupState] = useState<"idle" | "searching" | "found" | "empty">("idle");
  const formRef = useRef<HTMLFormElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Dernier identifiant réellement interrogé : sans cette mémoire, chaque frappe suivante
  // relancerait la même requête WLS pour un résultat identique.
  const dernierAppel = useRef("");
  const values = entry ?? emptyForm;
  const [identity, setIdentity] = useState({ epicId: entry?.epicId ?? "", discordId: entry?.discordId ?? "" });
  const priorLifts = useQuery(api.blacklist.priorLifts, {
    epicId: identity.epicId.length >= 24 ? identity.epicId : undefined,
    discordId: /^\d{15,21}$/.test(identity.discordId) ? identity.discordId : undefined,
  });

  // Le minuteur ne doit pas survivre à la fermeture de la fenêtre.
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  /**
   * Interroge WLS dès qu'un identifiant complet est saisi et complète les champs encore vides.
   * Ce qui a été tapé à la main n'est jamais écrasé : la recherche assiste, elle ne décide pas.
   */
  const autofill = async (field: "discordId" | "epicId") => {
    const form = formRef.current;
    if (!form) return;
    const read = (name: string) => (form.elements.namedItem(name) as HTMLInputElement | null)?.value.trim() ?? "";
    const value = read(field);
    const usable = field === "discordId" ? /^\d{15,21}$/.test(value) : value.replace(/-/g, "").length >= 24;
    if (!usable) return;
    if (dernierAppel.current === `${field}:${value}`) return;
    dernierAppel.current = `${field}:${value}`;
    setLookupState("searching");
    try {
      const result = await lookup(field === "discordId" ? { discordId: value } : { epicId: value });
      if (!result.found) { setLookupState("empty"); return; }
      const fill = (name: string, incoming?: string) => {
        const input = form.elements.namedItem(name) as HTMLInputElement | null;
        if (input && incoming && !input.value.trim()) input.value = incoming;
      };
      fill("discordId", result.discordId);
      fill("discordPseudo", result.discordName);
      fill("epicId", result.epicId);
      fill("epicPseudo", result.epicName ?? result.wlsName);
      setIdentity({ epicId: read("epicId"), discordId: read("discordId") });
      setLookupState("found");
    } catch {
      setLookupState("empty");
    }
  };
  /**
   * La recherche part dès que l'identifiant saisi est complet, sans attendre un changement de
   * champ : coller un Epic ID et rester dessus suffit. Le court délai laisse finir la frappe.
   */
  const planifier = (field: "discordId" | "epicId") => {
    const value = (formRef.current?.elements.namedItem(field) as HTMLInputElement | null)?.value.trim() ?? "";
    setIdentity(current => ({ ...current, [field]: value }));
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void autofill(field), 350);
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setLoading(true); setError("");
    const payload = {
      category: String(form.get("category")) as "cheat" | "team_up" | "insultes_admins",
      discordId: String(form.get("discordId") || "") || undefined,
      discordPseudo: String(form.get("discordPseudo") || "") || undefined,
      epicId: String(form.get("epicId") || "") || undefined,
      epicPseudo: String(form.get("epicPseudo") || "") || undefined,
      serversBan: String(form.get("serversBan") || "") || undefined,
      reason: String(form.get("reason") || "") || undefined,
      addedAt: fromDateInput(String(form.get("addedAt") || "")),
    };
    try { if (entry) await update({ id: entry._id, ...payload }); else await create(payload); onClose(); }
    catch (reason) { setError(errorMessage(reason)); setLoading(false); }
  };
  return <Modal title={entry ? "Modifier l’entrée" : "Ajouter une entrée"} description="Les identifiants sont normalisés et les doublons bloqués automatiquement." onClose={onClose}>
    {entry && <EntryOrigin id={entry._id} />}
    {!!priorLifts?.length && <Notice tone="warning">Ce joueur a déjà été retiré de la blacklist le {formatDate(priorLifts[0].liftedAt)}. Son ancien historique sera rattaché au nouveau ban.</Notice>}
    <form ref={formRef} onSubmit={submit} className="form-stack">
      <Field label="Catégorie"><select name="category" className="input" defaultValue={values.category}>{Object.entries(CATEGORY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></Field>
      <div className="form-grid">
        <Field label="Discord ID"><Input name="discordId" defaultValue={values.discordId} inputMode="numeric" onChange={() => planifier("discordId")} onBlur={() => void autofill("discordId")} /></Field>
        <Field label="Pseudo Discord"><Input name="discordPseudo" defaultValue={values.discordPseudo} /></Field>
        <Field label="Epic ID"><Input name="epicId" defaultValue={values.epicId} onChange={() => planifier("epicId")} onBlur={() => void autofill("epicId")} /></Field>
        <Field label="Pseudo Epic"><Input name="epicPseudo" defaultValue={values.epicPseudo} /></Field>
      </div>
      <div className="lookup-hint" role="status">
        {lookupState === "searching" && <><Loader2 size={13} className="spin" /> Recherche du compte sur WLS…</>}
        {lookupState === "found" && <><Sparkles size={13} /> Compte trouvé : les champs vides ont été complétés.</>}
        {lookupState === "empty" && <><Search size={13} /> Aucun compte WLS lié à cet identifiant.</>}
        {lookupState === "idle" && <>Saisissez un Discord ID ou un Epic ID : les autres champs sont récupérés automatiquement sur WLS.</>}
      </div>
      <div className="form-grid">
        <Field label="Serveurs de bannissement"><Input name="serversBan" defaultValue={values.serversBan} /></Field>
        <Field label="Date de la sanction"><Input name="addedAt" type="date" max={toDateInput(Date.now())} defaultValue={toDateInput(entry?.addedAt)} /></Field>
      </div>
      <Field label="Raison"><textarea name="reason" className="input textarea" defaultValue={values.reason} rows={3} /></Field>
      {error && <Notice tone="error">{error}</Notice>}
      <div className="modal-actions"><Button variant="secondary" onClick={onClose}>Annuler</Button><Button type="submit" loading={loading}>{entry ? "Enregistrer" : "Ajouter"}</Button></div>
    </form>
  </Modal>;
}

/** Une sanction levée ne suffit pas à débannir un joueur s'il garde un autre ban actif. */
function PlayerBlacklistStatus({ entry, entries }: { entry: Entry; entries?: Entry[] }) {
  const linked = useQuery(api.blacklist.linkedEntries, entries ? "skip" : { id: entry._id });
  const sanctions = entries ?? linked?.entries;
  if (!sanctions?.length) return <Badge tone="neutral">Sanction levée</Badge>;
  const active = sanctions.filter(item => item.status === "active").length;
  return active > 0
    ? <Badge tone="danger" title={`${active} sanction${active > 1 ? "s" : ""} encore active${active > 1 ? "s" : ""}`}>Toujours blacklisté</Badge>
    : <Badge tone="success" title="Toutes les sanctions de ce joueur sont levées ; son historique reste consultable.">Plus blacklisté</Badge>;
}

/** Forme comparable d'un identifiant : un Epic ID peut avoir été saisi avec ou sans tirets. */
function identityKey(kind: "epic" | "discord", value?: string) {
  const clean = kind === "epic" ? value?.replace(/-/g, "").toLowerCase() : value?.trim();
  return clean ? `${kind}:${clean}` : undefined;
}

const cleanPseudo = (value?: string) => value?.replace(/#0$/, "") || undefined;
const plural = (count: number, word: string) => `${count} ${word}${count > 1 ? "s" : ""}`;

type KnownIdentity = { key: string; value: string; pseudos: string[]; sanctions: number; partners: Set<string> };

/**
 * Chaque identifiant du joueur, une seule fois, avec les pseudos qu'il a portés et le nombre de
 * sanctions qui le citent. `partners` compte les identifiants de l'autre plateforme vus avec lui :
 * un Epic ID associé à quatre Discord différents est ce qui relie ces quatre comptes.
 */
function knownIdentities(entries: Entry[], kind: "epic" | "discord") {
  const found = new Map<string, KnownIdentity>();
  for (const item of entries) {
    const value = kind === "epic" ? item.epicId : item.discordId;
    const key = identityKey(kind, value);
    if (!key || !value) continue;
    const known = found.get(key) ?? { key, value, pseudos: [], sanctions: 0, partners: new Set<string>() };
    const pseudo = cleanPseudo(kind === "epic" ? item.epicPseudo : item.discordPseudo);
    if (pseudo && !known.pseudos.includes(pseudo)) known.pseudos.push(pseudo);
    known.sanctions += 1;
    const partner = kind === "epic" ? identityKey("discord", item.discordId) : identityKey("epic", item.epicId);
    if (partner) known.partners.add(partner);
    found.set(key, known);
  }
  return [...found.values()].sort((a, b) => b.sanctions - a.sanctions);
}

function KnownIdentityColumn({ kind, identities }: { kind: "epic" | "discord"; identities: KnownIdentity[] }) {
  const platform = kind === "epic" ? "Epic" : "Discord";
  const other = kind === "epic" ? "Discord" : "Epic";
  return (
    <div className="known-ids__column">
      <h4>{platform} <span className="heading-count">{identities.length}</span></h4>
      {identities.length === 0 ? <p className="known-ids__empty">Aucun identifiant {platform} enregistré.</p> : (
        <ul>
          {identities.map((identity) => (
            <li key={identity.key} className="known-id">
              <CopyValue value={identity.value} mono />
              <span className="known-id__meta">
                {identity.pseudos.length ? identity.pseudos.join(", ") : "Sans pseudo"} · {plural(identity.sanctions, "sanction")}
              </span>
              {identity.partners.size > 1 && (
                <span className="known-id__link">
                  <Link2 size={12} aria-hidden="true" /> Relie {identity.partners.size} comptes {other}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const JOURNAL_ACTIONS: Record<string, { label: string; icon: typeof Ban; tone: "danger" | "success" | "neutral" }> = {
  "blacklist.create": { label: "Nouveau bannissement", icon: Ban, tone: "danger" },
  "blacklist.active": { label: "Nouveau bannissement (réactivation)", icon: RotateCcw, tone: "danger" },
  "blacklist.lifted": { label: "Sanction levée", icon: ShieldOff, tone: "success" },
  "blacklist.update": { label: "Sanction modifiée", icon: Edit3, tone: "neutral" },
  "blacklist.delete": { label: "Fiche supprimée", icon: Trash2, tone: "neutral" },
  "blacklist.redated": { label: "Date de sanction corrigée", icon: CalendarClock, tone: "neutral" },
};

/**
 * L'historique d'un joueur se lit en trois temps : ses comptes (qui est-il ?), ses sanctions
 * (qu'a-t-il fait ?), puis le journal des modifications, replié parce qu'on y vient rarement.
 * Les identifiants ne sont jamais coupés en cours de valeur — un Epic ID scindé sur deux lignes
 * est invérifiable, et c'est précisément la preuve du rattachement.
 */
function LinkedAccountsModal({ entry, onClose }: { entry: Entry; onClose: () => void }) {
  const linked = useQuery(api.blacklist.linkedEntries, { id: entry._id });
  const history = useQuery(api.blacklist.history, { id: entry._id });
  const entries = linked?.entries ?? [];
  const epics = knownIdentities(entries, "epic");
  const discords = knownIdentities(entries, "discord");
  // Une ligne de sanction ne répète un identifiant que s'il distingue ce compte des autres :
  // un Epic ID commun aux cinq fiches figure déjà, une fois, dans « Comptes connus ».
  const showEpicId = epics.length > 1;
  const showDiscordId = discords.length > 1;
  const nom = cleanPseudo(entry.epicPseudo) ?? cleanPseudo(entry.discordPseudo);

  return <Modal
    className="modal--wide"
    title={nom ? `Historique de ${nom}` : "Historique du joueur"}
    description="Ses comptes connus et toutes ses sanctions, y compris celles qui ont été levées."
    onClose={onClose}
  >
    {linked === undefined ? <TableSkeleton /> : !linked || entries.length === 0 ? (
      <Notice tone="info">Aucun autre compte n’est rattaché à cette entrée.</Notice>
    ) : (
      <>
        <div className="player-summary" title={entries.some(item => item.status === "active")
          ? "Ce joueur conserve au moins une sanction active. Les sanctions levées restent dans son historique."
          : "Ce joueur n’est plus dans la blacklist active. Ses anciens bans restent consultables."}>
          <PlayerBlacklistStatus entry={entry} entries={entries} />
          {linked.summary.evasion && <Badge tone="evasion"><Layers size={11} aria-hidden="true" /> Contournement de ban</Badge>}
          <span className="player-summary__counts">
            <strong>{plural(linked.summary.accounts, "compte")}</strong> · {plural(linked.summary.entries, "sanction")}
          </span>
        </div>

        <section className="modal-section" aria-labelledby="accounts-title">
          <h3 id="accounts-title">Comptes connus</h3>
          <div className="known-ids">
            <KnownIdentityColumn kind="epic" identities={epics} />
            <KnownIdentityColumn kind="discord" identities={discords} />
          </div>
        </section>

        <section className="modal-section" aria-labelledby="sanctions-title">
          <h3 id="sanctions-title">Sanctions <span className="heading-count">{entries.length}</span></h3>
          <ol className="sanction-rows">
            {entries.map((item) => {
              const ouverte = item._id === entry._id;
              const levee = item.status === "lifted";
              const epicPseudo = cleanPseudo(item.epicPseudo);
              const discordPseudo = cleanPseudo(item.discordPseudo);
              return (
                <li key={item._id} className={`sanction-row${ouverte ? " sanction-row--current" : ""}`} aria-current={ouverte ? "true" : undefined}>
                  <div className="sanction-row__head">
                    <Badge tone={levee ? "success" : "danger"}>{levee ? "Levée" : "Active"}</Badge>
                    <Badge>{CATEGORY_LABELS[item.category]}</Badge>
                    {ouverte && <span className="sanction-row__current">Fiche ouverte</span>}
                    <time className="sanction-row__date" dateTime={new Date(item.addedAt).toISOString()} title={`Sanctionné le ${formatDate(item.addedAt)}`}>
                      {formatDateCompact(item.addedAt)}
                    </time>
                  </div>
                  <ReasonCell reason={item.reason || "Aucun motif enregistré."} />
                  <dl className="sanction-row__facts">
                    <div>
                      <dt>Epic</dt>
                      <dd>{epicPseudo ?? (!showEpicId || !item.epicId ? "—" : null)}{showEpicId && item.epicId && <code>{item.epicId}</code>}</dd>
                    </div>
                    <div>
                      <dt>Discord</dt>
                      <dd>{discordPseudo ?? (!showDiscordId || !item.discordId ? "—" : null)}{showDiscordId && item.discordId && <code>{item.discordId}</code>}</dd>
                    </div>
                    <div>
                      <dt>Serveurs</dt>
                      <dd>{item.serversBan || "non précisés"}</dd>
                    </div>
                  </dl>
                  {levee && item.liftedAt && (
                    <p className="sanction-row__lift">
                      <ShieldOff size={13} aria-hidden="true" />
                      Levée le {formatDateCompact(item.liftedAt)}{item.liftReason ? ` — ${item.liftReason}` : ""}
                    </p>
                  )}
                </li>
              );
            })}
          </ol>
        </section>
      </>
    )}

    {history !== undefined && history.length > 0 && (
      <details className="modal-section journal-disclosure">
        <summary>
          <span>Journal permanent</span> <span className="heading-count">{history.length}</span>
          <ChevronDown size={15} aria-hidden="true" />
        </summary>
        <p className="modal-section__hint">Chaque bannissement, levée et modification, conservé même après la suppression d’une fiche.</p>
        <ol className="journal">
          {history.map(event => {
            const presentation = buildAuditPresentation({ ...event, entityType: "blacklist", metadata: event.metadata });
            const meta = JOURNAL_ACTIONS[event.action] ?? { label: actionLabel(event.action), icon: History, tone: "neutral" as const };
            const Icon = meta.icon;
            const profile = event.profile as Partial<Entry> | undefined;
            const pseudo = cleanPseudo(profile?.epicPseudo) ?? cleanPseudo(profile?.discordPseudo);
            const summary = pseudo && profile?.category ? `${pseudo} · ${CATEGORY_LABELS[profile.category]}` : presentation.summary;
            // Le motif en vigueur au moment de l'événement : il a pu changer depuis.
            const details = profile?.reason && !presentation.details.some(detail => detail.label === "Raison")
              ? [{ label: "Motif du ban", value: profile.reason }, ...presentation.details]
              : presentation.details;
            const changes = details.filter(detail => detail.before !== undefined).length;
            return (
              <li key={event._id} className="journal__item">
                <span className={`journal__icon journal__icon--${meta.tone}`} aria-hidden="true"><Icon size={14} /></span>
                <div className="journal__body">
                  <div className="journal__head">
                    <strong>{meta.label}</strong>
                    <time dateTime={new Date(event.createdAt).toISOString()}>{formatDate(event.createdAt)}</time>
                  </div>
                  <p className="journal__summary">{summary}</p>
                  {details.length > 0 && (
                    <details className="journal__details">
                      <summary>{changes ? `${plural(changes, "modification")} · avant / après` : "Voir les détails"} <ChevronDown size={14} aria-hidden="true" /></summary>
                      <dl>
                        {details.map((detail, index) => (
                          <div key={`${detail.label}-${index}`} className={detail.before !== undefined ? "journal__detail journal__detail--change" : (detail.value?.length ?? 0) > 48 ? "journal__detail journal__detail--wide" : "journal__detail"}>
                            <dt>{detail.label}</dt>
                            {detail.before !== undefined
                              ? <dd className="journal__change"><span><small>Avant</small>{detail.before}</span><span><small>Après</small>{detail.after}</span></dd>
                              : <dd>{detail.value}</dd>}
                          </div>
                        ))}
                      </dl>
                    </details>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </details>
    )}
    <div className="modal-actions"><Button variant="secondary" onClick={onClose}>Fermer</Button></div>
  </Modal>;
}

export function BlacklistPage() {
  const me = useQuery(api.users.me);
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("q") ?? "";
  const deferredSearch = useDeferredValue(search);
  const [category, setCategory] = useState<"all" | "cheat" | "team_up" | "insultes_admins">("all");
  const [status, setStatus] = useState<"active" | "lifted" | "all">("all");
  const [evasion, setEvasion] = useState<"all" | "only" | "exclude">("all");
  const [order, setOrder] = useState<"desc" | "asc">("desc");
  const [editing, setEditing] = useState<Entry | "new" | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Entry | null>(null);
  const [linkedFor, setLinkedFor] = useState<Entry | null>(null);
  const [rowError, setRowError] = useState("");
  const [pendingRow, setPendingRow] = useState<Id<"blacklist"> | null>(null);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [lifting, setLifting] = useState(false);
  const [redating, setRedating] = useState(false);
  const liftMany = useMutation(api.blacklist.liftMany);
  const setEntryStatus = useMutation(api.blacklist.setStatus);
  const remove = useMutation(api.blacklist.remove);

  const { results, status: paginationStatus, loadMore } = usePaginatedQuery(api.blacklist.list, {
    search: deferredSearch || undefined,
    category: category === "all" ? undefined : category,
    status: status === "all" ? undefined : status,
    evasion: evasion === "all" ? undefined : evasion,
    order,
  }, { initialNumItems: 30 });
  const admin = me?.role === "admin";

  useEffect(() => { setRowError(""); }, [deferredSearch, category, status, evasion]);

  const toggleStatus = async (entry: Entry) => {
    setPendingRow(entry._id); setRowError("");
    try {
      await setEntryStatus({
        id: entry._id,
        status: entry.status === "active" ? "lifted" : "active",
        reason: entry.status === "active" ? "Levée manuelle" : undefined,
      });
    } catch (reason) { setRowError(errorMessage(reason)); }
    finally { setPendingRow(null); }
  };

  return <>
    <PageHeader
      title="Blacklist"
      description="Recherchez les joueurs blacklistés ou débannis et consultez leur historique, même après une levée."
      actions={admin && (
        <>
          <Button variant="secondary" onClick={() => setRedating(true)}><CalendarClock size={15} /> Dater par capture</Button>
          <Button variant="primary" onClick={() => setEditing("new")}><Plus size={16} /> Ajouter</Button>
        </>
      )}
    />
    <Card className="toolbar">
      <div className="search-field">
        <Search size={17} aria-hidden="true" />
        <Input
          value={search}
          onChange={(event) => {
            setStatus("all");
            setSearchParams(event.target.value ? { q: event.target.value } : {}, { replace: true });
          }}
          placeholder="Epic ID, Discord ID, pseudo, raison…"
          aria-label="Rechercher un joueur"
        />
      </div>
      <select className="input" value={category} onChange={(event) => setCategory(event.target.value as typeof category)} aria-label="Catégorie">
        <option value="all">Toutes les catégories</option>
        {Object.entries(CATEGORY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </select>
      <select className="input" value={evasion} onChange={(event) => setEvasion(event.target.value as typeof evasion)} aria-label="Contournements de ban">
        <option value="all">Avec les contournements</option>
        <option value="exclude">Sans les contournements</option>
        <option value="only">Contournements uniquement</option>
      </select>
      <select className="input" value={order} onChange={(event) => setOrder(event.target.value as typeof order)} aria-label="Ordre d’ajout">
        <option value="desc">Plus récentes</option>
        <option value="asc">Plus anciennes</option>
      </select>
      <div className="segmented" role="group" aria-label="Statut de la sanction">
        <button type="button" aria-pressed={status === "active"} onClick={() => setStatus("active")} className={status === "active" ? "active" : ""}>Actives</button>
        <button type="button" aria-pressed={status === "lifted"} onClick={() => setStatus("lifted")} className={status === "lifted" ? "active" : ""}>Sanctions levées</button>
        <button type="button" aria-pressed={status === "all"} onClick={() => setStatus("all")} className={status === "all" ? "active" : ""}>Tous / historique</button>
      </div>
    </Card>
    {rowError && <Notice tone="error">{rowError}</Notice>}
    {admin && selection.size > 0 && (
      <Card className="selection-bar">
        <span><strong>{selection.size}</strong> sanction{selection.size > 1 ? "s" : ""} sélectionnée{selection.size > 1 ? "s" : ""}</span>
        <span className="selection-bar__actions">
          <Button variant="secondary" onClick={() => setSelection(new Set())}>Tout désélectionner</Button>
          <Button
            loading={lifting}
            onClick={() => {
              setLifting(true); setRowError("");
              void liftMany({ ids: [...selection] as Id<"blacklist">[] })
                .then(() => setSelection(new Set()))
                .catch((reason) => setRowError(errorMessage(reason)))
                .finally(() => setLifting(false));
            }}
          >
            <ShieldOff size={15} /> Lever la sélection
          </Button>
        </span>
      </Card>
    )}
    {paginationStatus === "LoadingFirstPage" ? <TableSkeleton /> : results.length === 0 ? (
      <EmptyState
        title="Aucune entrée"
        description={evasion === "only"
          ? "Aucun contournement de ban ne correspond à ces filtres."
          : "Aucun résultat ne correspond aux filtres actuels."}
      />
    ) : (
      <Card className="table-card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th scope="col">Compte Epic</th>
                <th scope="col">Compte Discord</th>
                <th scope="col">Sanction</th>
                <th scope="col">Raison</th>
                <th scope="col">Ajout</th>
                {admin && <th scope="col" className="align-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {results.map((entry) => (
                <tr key={entry._id} className={`${entry.evasion ? "row--evasion " : ""}${entry.status === "lifted" ? "row--lifted" : ""}`.trim() || undefined}>
                  {/* Pseudo et identifiant d'une même plateforme dans une seule colonne : quatre
                      colonnes pour deux comptes poussaient les actions hors du cadre. */}
                  <td>
                    <div className="account-cell">
                      {entry.epicPseudo && <CopyValue value={entry.epicPseudo} />}
                      {entry.epicId && <CopyValue value={entry.epicId} mono />}
                      {!entry.epicPseudo && !entry.epicId && <span className="copy-value copy-value--empty">—</span>}
                      <div className="identity-cell__links">
                        <button type="button" className="history-link" onClick={() => setLinkedFor(entry)} title="Consulter tous les bans et leurs levées">
                          <History size={13} aria-hidden="true" /> Historique
                        </button>
                        {entry.evasion && (
                          <button
                            type="button"
                            className="evasion-tag"
                            title={`Contournement de ban : ${entry.linkedAccounts} comptes rattachés à ce joueur. Cliquez pour les voir.`}
                            onClick={() => setLinkedFor(entry)}
                          >
                            <Layers size={11} aria-hidden="true" />
                            {entry.linkedAccounts} comptes
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="account-cell">
                      {cleanPseudo(entry.discordPseudo) && <CopyValue value={cleanPseudo(entry.discordPseudo)} />}
                      {entry.discordId && <CopyValue value={entry.discordId} mono />}
                      {!cleanPseudo(entry.discordPseudo) && !entry.discordId && <span className="copy-value copy-value--empty">—</span>}
                    </div>
                  </td>
                  <td>
                    <div className="status-cell">
                      <Badge>{CATEGORY_LABELS[entry.category]}</Badge>
                      {entry.status === "lifted" ? (
                        <>
                          <PlayerBlacklistStatus entry={entry} />
                          <small>Sanction levée le {formatDate(entry.liftedAt)}</small>
                          {entry.liftReason && <small>{entry.liftReason}</small>}
                        </>
                      ) : (
                        <><Badge tone="danger">Blacklisté</Badge>{entry.previouslyLiftedAt && <small>Déjà retiré le {formatDate(entry.previouslyLiftedAt)}</small>}</>
                      )}
                    </div>
                  </td>
                  <td>
                    <ReasonCell reason={entry.reason} />
                    {entry.serversBan && <small className="servers-line">Serveurs : {entry.serversBan}</small>}
                  </td>
                  <td><span className="nowrap" title={formatDate(entry.addedAt)}>{formatMonthYear(entry.addedAt)}</span></td>
                  {admin && (
                    <td>
                      <div className="row-actions">
                        <button type="button" onClick={() => setEditing(entry)} className="icon-button" title="Modifier" aria-label="Modifier l’entrée"><Edit3 size={16} /></button>
                        <button
                          type="button"
                          disabled={pendingRow === entry._id}
                          onClick={() => void toggleStatus(entry)}
                          className="icon-button"
                          title={entry.status === "active" ? "Lever la sanction" : "Réactiver la sanction"}
                          aria-label={entry.status === "active" ? "Lever la sanction" : "Réactiver la sanction"}
                        >{entry.status === "active" ? <Ban size={16} /> : <RotateCcw size={16} />}</button>
                        <button type="button" onClick={() => setConfirmDelete(entry)} className="icon-button icon-button--danger" title="Supprimer" aria-label="Supprimer l’entrée"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {paginationStatus === "CanLoadMore" && <div className="load-more"><Button variant="secondary" onClick={() => loadMore(30)}>Afficher plus</Button></div>}
      </Card>
    )}
    {editing && <EntryForm entry={editing === "new" ? undefined : editing} onClose={() => setEditing(null)} />}
    {linkedFor && <LinkedAccountsModal entry={linkedFor} onClose={() => setLinkedFor(null)} />}
    {redating && <RedateModal onClose={() => setRedating(false)} />}
    {confirmDelete && (
      <ConfirmDialog
        title="Supprimer définitivement"
        description="Cette entrée sera retirée du référentiel. L’action est tracée mais irréversible. Pour conserver l’historique, préférez la levée de sanction."
        target={`${confirmDelete.epicPseudo || confirmDelete.discordPseudo || "Identité sans pseudo"} · ${confirmDelete.epicId || confirmDelete.discordId || "sans identifiant"}`}
        confirmLabel="Supprimer"
        onConfirm={() => remove({ id: confirmDelete._id })}
        onClose={() => setConfirmDelete(null)}
      />
    )}
  </>;
}
